import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';

const MONTHS_DE = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
];

const MONTHS_DE_SHORT = [
    'Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun',
    'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'
];

const DAYS_HEADER = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

const THEME_ORANGE = '#E55C1A';
const THEME_ORANGE_LIGHT = '#c2410c';
const THEME_ORANGE_DARK = '#c94f16';
const THEME_ORANGE_SOFT_DARK = 'rgba(229, 92, 26, 0.16)';
const THEME_ORANGE_SOFT_LIGHT = 'rgba(194, 65, 12, 0.09)';
const THEME_ORANGE_RANGE_DARK = 'rgba(229, 92, 26, 0.20)';
const THEME_ORANGE_RANGE_LIGHT = 'rgba(194, 65, 12, 0.12)';
const DARK_PANEL = '#0D151F';
const DARK_PANEL_DEEP = '#070C12';
const DARK_PANEL_HOVER = '#132033';
const DARK_BORDER = '#404040';
const DARK_TEXT = '#f8fafc';
const DARK_MUTED = '#94a3b8';

const toISO = (d) => {
    const tz = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tz).toISOString().split('T')[0];
};

const parseISO = (s) => {
    if (!s) return null;
    const [y, m, d] = s.split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
};

const normalizeRange = (start, end) => {
    const startDate = parseISO(start);
    const endDate = parseISO(end);
    if (!startDate || !endDate) return { start, end };
    return startDate <= endDate
        ? { start, end }
        : { start: end, end: start };
};

const formatDisplay = (isoStr) => {
    if (!isoStr) return '—';
    const d = parseISO(isoStr);
    if (!d) return '—';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = String(d.getFullYear()).slice(-2);
    return `${day}.${month}.${year}`;
};

const isSameDay = (a, b) => (
    a && b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
);

const isInRange = (d, start, end) => d && start && end && d > start && d < end;

const getMonthStartISO = (year, month) => toISO(new Date(year, month, 1));
const getMonthEndISO = (year, month) => toISO(new Date(year, month + 1, 0));
const getYearStartISO = (year) => toISO(new Date(year, 0, 1));
const getYearEndISO = (year) => toISO(new Date(year, 11, 31));

function Calendar({
    year,
    month,
    startDate,
    endDate,
    hoverDate,
    onDayClick,
    onDayHover,
    isDarkMode,
}) {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDow = (firstDay.getDay() + 6) % 7;
    const totalDays = lastDay.getDate();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= totalDays; d++) cells.push(new Date(year, month, d));

    const start = parseISO(startDate);
    const end = parseISO(endDate);
    const hover = hoverDate;
    const rangeEnd = end || hover;

    return (
        <div className="w-full">
            <div className="grid grid-cols-7 mb-2">
                {DAYS_HEADER.map((d) => (
                    <div key={d} className="text-center text-[11px] font-medium text-gray-500 py-1">
                        {d}
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-7 gap-y-1">
                {cells.map((date, idx) => {
                    if (!date) return <div key={`empty-${idx}`} />;

                    const isStart = isSameDay(date, start);
                    const isEnd = end && isSameDay(date, end);
                    const inRange = start && rangeEnd && isInRange(date, start, rangeEnd);
                    const isHoverEnd = !end && hover && isSameDay(date, hover);
                    const isToday = isSameDay(date, today);
                    const rangeBg = isDarkMode ? THEME_ORANGE_RANGE_DARK : THEME_ORANGE_RANGE_LIGHT;

                    let dotClass = 'relative z-10 w-8 h-8 flex items-center justify-center rounded-full text-[13px] font-normal transition-all ';

                    if (isStart || (isEnd && !isHoverEnd)) {
                        dotClass += 'text-white font-medium ';
                    } else if (isHoverEnd) {
                        dotClass += isDarkMode
                            ? 'bg-transparent border border-orange-300 text-orange-100 '
                            : 'bg-transparent border border-[#c2410c] text-[#9a3412] ';
                    } else if (isToday) {
                        dotClass += isDarkMode ? 'text-[#E55C1A] font-medium ' : 'text-[#c2410c] font-medium ';
                    } else if (inRange) {
                        dotClass += isDarkMode ? 'text-orange-50 ' : 'text-[#334155] ';
                    } else {
                        dotClass += isDarkMode ? 'text-gray-300 hover:bg-white/10 ' : 'text-[#536175] hover:bg-[#eef3f8] ';
                    }

                    return (
                        <div
                            key={date.toISOString()}
                            className="relative h-9 flex items-center justify-center text-sm cursor-pointer select-none transition-all"
                            onClick={() => onDayClick(date)}
                            onMouseEnter={() => onDayHover(date)}
                            onMouseLeave={() => onDayHover(null)}
                        >
                            {(isStart || inRange || isEnd || isHoverEnd) && (
                                <div
                                    className="absolute inset-y-1 left-0 right-0"
                                    style={{
                                        background: rangeBg,
                                        borderTopLeftRadius: isStart ? 999 : 0,
                                        borderBottomLeftRadius: isStart ? 999 : 0,
                                        borderTopRightRadius: isEnd || isHoverEnd ? 999 : 0,
                                        borderBottomRightRadius: isEnd || isHoverEnd ? 999 : 0,
                                    }}
                                />
                            )}

                            <div
                                className={dotClass}
                                style={
                                    isStart || (isEnd && !isHoverEnd)
                                        ? {
                                            background: isDarkMode ? THEME_ORANGE : THEME_ORANGE_LIGHT,
                                            boxShadow: isDarkMode ? '0 6px 14px rgba(229, 92, 26, 0.22)' : '0 6px 14px rgba(194, 65, 12, 0.18)',
                                        }
                                        : undefined
                                }
                            >
                                {date.getDate()}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function MonthPicker({
    year,
    startDate,
    endDate,
    onMonthClick,
    onYearClick,
    onPrevYear,
    onNextYear,
    isDarkMode,
}) {
    const start = parseISO(startDate);
    const end = parseISO(endDate);
    const today = new Date();

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <button
                    onClick={onPrevYear}
                    style={navButtonStyle(isDarkMode)}
                    onMouseEnter={(e) => e.currentTarget.style.background = isDarkMode ? DARK_PANEL_HOVER : '#eef3f8'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                    ‹
                </button>

                <button
                    onClick={onYearClick}
                    style={{
                        border: `1px solid ${isDarkMode ? DARK_BORDER : '#d8e0ea'}`,
                        background: isDarkMode ? DARK_PANEL_DEEP : '#ffffff',
                        color: isDarkMode ? DARK_TEXT : '#172033',
                        borderRadius: 12,
                        padding: '7px 16px',
                        fontWeight: 600,
                        fontSize: 15,
                        cursor: 'pointer',
                    }}
                >
                    {year}
                </button>

                <button
                    onClick={onNextYear}
                    style={navButtonStyle(isDarkMode)}
                    onMouseEnter={(e) => e.currentTarget.style.background = isDarkMode ? DARK_PANEL_HOVER : '#eef3f8'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                    ›
                </button>
            </div>

            <div className="grid grid-cols-3 gap-2.5">
                {MONTHS_DE_SHORT.map((label, idx) => {
                    const monthStart = new Date(year, idx, 1);
                    const monthEnd = new Date(year, idx + 1, 0);
                    const selectedStart = start && start.getFullYear() === year && start.getMonth() === idx;
                    const selectedEnd = end && end.getFullYear() === year && end.getMonth() === idx;
                    const insideRange = start && end && monthStart <= end && monthEnd >= start;
                    const currentMonth = today.getFullYear() === year && today.getMonth() === idx;
                    const selected = selectedStart || selectedEnd;

                    return (
                        <button
                            key={label}
                            onClick={() => onMonthClick(idx)}
                            style={{
                                border: selected
                                    ? `1px solid ${isDarkMode ? THEME_ORANGE : THEME_ORANGE_LIGHT}`
                                    : insideRange
                                        ? `1px solid ${isDarkMode ? 'rgba(229,92,26,0.28)' : 'rgba(229,92,26,0.18)'}`
                                        : `1px solid ${isDarkMode ? DARK_BORDER : '#d8e0ea'}`,
                                background: selected
                                    ? (isDarkMode ? THEME_ORANGE : THEME_ORANGE_LIGHT)
                                    : insideRange
                                        ? (isDarkMode ? THEME_ORANGE_SOFT_DARK : THEME_ORANGE_SOFT_LIGHT)
                                        : (isDarkMode ? DARK_PANEL_DEEP : '#ffffff'),
                                color: selected
                                    ? '#fff'
                                    : currentMonth
                                        ? (isDarkMode ? THEME_ORANGE : THEME_ORANGE_LIGHT)
                                        : (isDarkMode ? DARK_TEXT : '#536175'),
                                borderRadius: 12,
                                minHeight: 44,
                                fontWeight: selected || currentMonth ? 600 : 500,
                                fontSize: 13,
                                cursor: 'pointer',
                                transition: 'all 0.15s ease',
                                boxShadow: selected ? '0 6px 14px rgba(229, 92, 26, 0.18)' : 'none',
                            }}
                            onMouseEnter={(e) => {
                                if (!selected) e.currentTarget.style.borderColor = isDarkMode ? THEME_ORANGE : THEME_ORANGE_LIGHT;
                            }}
                            onMouseLeave={(e) => {
                                if (!selected) {
                                    e.currentTarget.style.borderColor = insideRange
                                        ? (isDarkMode ? 'rgba(229,92,26,0.28)' : 'rgba(229,92,26,0.18)')
                                        : (isDarkMode ? DARK_BORDER : '#d8e0ea');
                                }
                            }}
                        >
                            {label}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

function YearPicker({
    baseYear,
    startDate,
    endDate,
    onYearClick,
    onPrevYears,
    onNextYears,
    isDarkMode,
}) {
    const start = parseISO(startDate);
    const end = parseISO(endDate);
    const today = new Date();
    const firstYear = Math.floor(baseYear / 12) * 12;
    const years = Array.from({ length: 12 }, (_, i) => firstYear + i);

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <button
                    onClick={onPrevYears}
                    style={navButtonStyle(isDarkMode)}
                    onMouseEnter={(e) => e.currentTarget.style.background = isDarkMode ? DARK_PANEL_HOVER : '#eef3f8'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                    ‹
                </button>

                <span style={{ fontWeight: 600, fontSize: 15, color: isDarkMode ? DARK_TEXT : '#172033' }}>
                    {firstYear} bis {firstYear + 11}
                </span>

                <button
                    onClick={onNextYears}
                    style={navButtonStyle(isDarkMode)}
                    onMouseEnter={(e) => e.currentTarget.style.background = isDarkMode ? DARK_PANEL_HOVER : '#eef3f8'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                    ›
                </button>
            </div>

            <div className="grid grid-cols-3 gap-2.5">
                {years.map((year) => {
                    const yearStart = new Date(year, 0, 1);
                    const yearEnd = new Date(year, 11, 31);
                    const selectedStart = start && start.getFullYear() === year;
                    const selectedEnd = end && end.getFullYear() === year;
                    const insideRange = start && end && yearStart <= end && yearEnd >= start;
                    const currentYear = today.getFullYear() === year;
                    const selected = selectedStart || selectedEnd;

                    return (
                        <button
                            key={year}
                            onClick={() => onYearClick(year)}
                            style={{
                                border: selected
                                    ? `1px solid ${isDarkMode ? THEME_ORANGE : THEME_ORANGE_LIGHT}`
                                    : insideRange
                                        ? `1px solid ${isDarkMode ? 'rgba(229,92,26,0.28)' : 'rgba(229,92,26,0.18)'}`
                                        : `1px solid ${isDarkMode ? DARK_BORDER : '#d8e0ea'}`,
                                background: selected
                                    ? (isDarkMode ? THEME_ORANGE : THEME_ORANGE_LIGHT)
                                    : insideRange
                                        ? (isDarkMode ? THEME_ORANGE_SOFT_DARK : THEME_ORANGE_SOFT_LIGHT)
                                        : (isDarkMode ? DARK_PANEL_DEEP : '#ffffff'),
                                color: selected
                                    ? '#fff'
                                    : currentYear
                                        ? (isDarkMode ? THEME_ORANGE : THEME_ORANGE_LIGHT)
                                        : (isDarkMode ? DARK_TEXT : '#536175'),
                                borderRadius: 12,
                                minHeight: 44,
                                fontWeight: selected || currentYear ? 600 : 500,
                                fontSize: 13,
                                cursor: 'pointer',
                                transition: 'all 0.15s ease',
                                boxShadow: selected ? '0 6px 14px rgba(229, 92, 26, 0.18)' : 'none',
                            }}
                            onMouseEnter={(e) => {
                                if (!selected) e.currentTarget.style.borderColor = isDarkMode ? THEME_ORANGE : THEME_ORANGE_LIGHT;
                            }}
                            onMouseLeave={(e) => {
                                if (!selected) {
                                    e.currentTarget.style.borderColor = insideRange
                                        ? (isDarkMode ? 'rgba(229,92,26,0.28)' : 'rgba(229,92,26,0.18)')
                                        : (isDarkMode ? DARK_BORDER : '#d8e0ea');
                                }
                            }}
                        >
                            {year}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

const PRESETS = [
    { key: 'today', label: 'Heute' },
    { key: 'thisWeek', label: 'Diese Woche' },
    { key: 'lastWeek', label: 'Letzte Woche' },
    { key: 'last7', label: 'Letzte 7 Tage' },
    { key: 'thisMonth', label: 'Dieser Monat' },
    { key: 'lastMonth', label: 'Letzter Monat' },
    { key: 'thisYear', label: 'Dieses Jahr' },
];

function getAnchorDate(anchorDate) {
    const parsed = parseISO(anchorDate);
    const anchor = parsed || new Date();
    anchor.setHours(0, 0, 0, 0);
    return anchor;
}

function applyPreset(key, anchorDate) {
    const today = getAnchorDate(anchorDate);
    today.setHours(0, 0, 0, 0);
    const todayStr = toISO(today);

    switch (key) {
        case 'today':
            return { start: todayStr, end: todayStr };
        case 'thisWeek': {
            const dow = (today.getDay() + 6) % 7;
            const mon = new Date(today);
            mon.setDate(today.getDate() - dow);
            return { start: toISO(mon), end: todayStr };
        }
        case 'lastWeek': {
            const dow = (today.getDay() + 6) % 7;
            const mon = new Date(today);
            mon.setDate(today.getDate() - dow - 7);
            const sun = new Date(mon);
            sun.setDate(mon.getDate() + 6);
            return { start: toISO(mon), end: toISO(sun) };
        }
        case 'last7': {
            const d = new Date(today);
            d.setDate(today.getDate() - 6);
            return { start: toISO(d), end: todayStr };
        }
        case 'thisMonth': {
            const first = new Date(today.getFullYear(), today.getMonth(), 1);
            return { start: toISO(first), end: todayStr };
        }
        case 'lastMonth': {
            const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            const last = new Date(today.getFullYear(), today.getMonth(), 0);
            return { start: toISO(first), end: toISO(last) };
        }
        case 'thisYear': {
            const first = new Date(today.getFullYear(), 0, 1);
            return { start: toISO(first), end: todayStr };
        }
        default:
            return null;
    }
}

function findMatchingPreset(start, end, anchorDate) {
    if (!start || !end) return null;
    const current = normalizeRange(start, end);

    return PRESETS.find((preset) => {
        const presetRange = applyPreset(preset.key, anchorDate);
        if (!presetRange) return false;
        const normalizedPreset = normalizeRange(presetRange.start, presetRange.end);
        return normalizedPreset.start === current.start && normalizedPreset.end === current.end;
    }) || null;
}

function navButtonStyle(isDarkMode) {
    return {
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        color: isDarkMode ? DARK_TEXT : '#536175',
        padding: '6px 10px',
        borderRadius: 10,
        fontSize: 20,
        fontWeight: 300,
        lineHeight: 1,
        transition: 'background 0.15s ease',
    };
}

function ViewToggle({ viewMode, setViewMode, isDarkMode }) {
    const items = [
        { key: 'day', label: 'Tage' },
        { key: 'month', label: 'Monate' },
        { key: 'year', label: 'Jahre' },
    ];

    return (
        <div
            style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 3,
                background: isDarkMode ? DARK_PANEL_DEEP : '#eef3f8',
                border: `1px solid ${isDarkMode ? DARK_BORDER : '#d8e0ea'}`,
                borderRadius: 13,
                padding: 3,
                marginBottom: 18,
            }}
        >
            {items.map((item) => {
                const active = viewMode === item.key;
                return (
                    <button
                        key={item.key}
                        onClick={() => setViewMode(item.key)}
                        style={{
                            border: 'none',
                            borderRadius: 10,
                            padding: '7px 10px',
                            fontSize: 12,
                            fontWeight: active ? 600 : 500,
                            cursor: 'pointer',
                            background: active
                                ? (isDarkMode ? THEME_ORANGE : '#ffffff')
                                : 'transparent',
                            color: active
                                ? (isDarkMode ? '#ffffff' : THEME_ORANGE_LIGHT)
                                : (isDarkMode ? DARK_MUTED : '#536175'),
                            boxShadow: active
                                ? (isDarkMode ? '0 4px 10px rgba(0,0,0,0.25)' : '0 3px 8px rgba(15, 23, 42, 0.08)')
                                : 'none',
                            transition: 'all 0.15s ease',
                        }}
                    >
                        {item.label}
                    </button>
                );
            })}
        </div>
    );
}

export default function DateRangePicker({
    startDate,
    endDate,
    onChange,
    isDarkMode,
    label,
    anchorDate,
}) {
    const [open, setOpen] = useState(false);
    const [tempStart, setTempStart] = useState(startDate || null);
    const [tempEnd, setTempEnd] = useState(endDate || null);
    const [selecting, setSelecting] = useState(false);
    const [hoverDate, setHoverDate] = useState(null);
    const [viewMode, setViewMode] = useState('day');

    const today = useMemo(() => new Date(), []);
    const initialStart = tempStart ? parseISO(tempStart) : null;

    const [calYear, setCalYear] = useState(initialStart?.getFullYear() || today.getFullYear());
    const [calMonth, setCalMonth] = useState(initialStart?.getMonth() ?? today.getMonth());
    const [yearPage, setYearPage] = useState(initialStart?.getFullYear() || today.getFullYear());

    const ref = useRef(null);

    useEffect(() => {
        setTempStart(startDate || null);
        setTempEnd(endDate || null);
    }, [startDate, endDate]);

    useEffect(() => {
        if (!open) return;
        const handleClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [open]);

    const handleDayClick = useCallback((date) => {
        if (!selecting) {
            setTempStart(toISO(date));
            setTempEnd(null);
            setSelecting(true);
            return;
        }

        if (date < parseISO(tempStart)) {
            setTempEnd(tempStart);
            setTempStart(toISO(date));
        } else {
            setTempEnd(toISO(date));
        }
        setSelecting(false);
    }, [selecting, tempStart]);

    const handleMonthClick = (monthIndex) => {
        const monthStart = getMonthStartISO(calYear, monthIndex);
        const monthEnd = getMonthEndISO(calYear, monthIndex);

        if (!selecting) {
            setTempStart(monthStart);
            setTempEnd(null);
            setSelecting(true);
            setCalMonth(monthIndex);
            return;
        }

        const start = parseISO(tempStart);
        const candidateEnd = parseISO(monthEnd);

        if (start && candidateEnd < start) {
            setTempStart(monthStart);
            setTempEnd(getMonthEndISO(start.getFullYear(), start.getMonth()));
        } else {
            setTempEnd(monthEnd);
        }

        setSelecting(false);
        setCalMonth(monthIndex);
    };

    const handleYearClick = (year) => {
        const yearStart = getYearStartISO(year);
        const yearEnd = getYearEndISO(year);

        if (!selecting) {
            setTempStart(yearStart);
            setTempEnd(null);
            setSelecting(true);
            setCalYear(year);
            setYearPage(year);
            return;
        }

        const start = parseISO(tempStart);
        const candidateEnd = parseISO(yearEnd);

        if (start && candidateEnd < start) {
            setTempStart(yearStart);
            setTempEnd(getYearEndISO(start.getFullYear()));
        } else {
            setTempEnd(yearEnd);
        }

        setSelecting(false);
        setCalYear(year);
        setYearPage(year);
    };

    const handleApply = () => {
        if (tempStart && tempEnd) {
            const range = normalizeRange(tempStart, tempEnd);
            setTempStart(range.start);
            setTempEnd(range.end);
            const matchingPreset = findMatchingPreset(range.start, range.end, anchorDate);
            onChange(range.start, range.end, matchingPreset?.key || 'custom');
            setOpen(false);
            setSelecting(false);
            setHoverDate(null);
        }
    };

    const handlePreset = (key) => {
        const result = applyPreset(key, anchorDate);
        if (result) {
            setTempStart(result.start);
            setTempEnd(result.end);
            setSelecting(false);
            setHoverDate(null);

            const s = parseISO(result.start);
            if (s) {
                setCalYear(s.getFullYear());
                setCalMonth(s.getMonth());
                setYearPage(s.getFullYear());
            }

            onChange(result.start, result.end, key);
            setOpen(false);
        }
    };

    const handleReset = () => {
        const anchorToday = getAnchorDate(anchorDate);
        const first = new Date(anchorToday.getFullYear(), anchorToday.getMonth(), 1);
        const start = toISO(first);
        const end = toISO(anchorToday);

        setTempStart(start);
        setTempEnd(end);
        setSelecting(false);
        setHoverDate(null);
        setViewMode('day');
        setCalYear(anchorToday.getFullYear());
        setCalMonth(anchorToday.getMonth());
        setYearPage(anchorToday.getFullYear());

        onChange(start, end, 'thisMonth');
        setOpen(false);
    };

    const prevMonth = () => {
        if (calMonth === 0) {
            setCalMonth(11);
            setCalYear((y) => y - 1);
        } else {
            setCalMonth((m) => m - 1);
        }
    };

    const nextMonth = () => {
        if (calMonth === 11) {
            setCalMonth(0);
            setCalYear((y) => y + 1);
        } else {
            setCalMonth((m) => m + 1);
        }
    };

    const activePreset = useMemo(
        () => findMatchingPreset(startDate, endDate, anchorDate),
        [startDate, endDate, anchorDate]
    );

    const triggerLabel = (startDate && endDate)
        ? activePreset
            ? activePreset.label
            : `${formatDisplay(startDate)} – ${formatDisplay(endDate)}`
        : 'Datum wählen';

    const previewStart = tempStart ? parseISO(tempStart) : null;
    const previewEnd = tempEnd ? parseISO(tempEnd) : null;

    const headerLabel = previewStart && previewEnd
        ? `${formatDisplay(tempStart)} – ${formatDisplay(tempEnd)}`
        : previewStart
            ? `${formatDisplay(tempStart)} bis ...`
            : 'Wähle Start...';

    const dropBg = isDarkMode ? DARK_PANEL : '#ffffff';
    const dropBorder = isDarkMode ? DARK_BORDER : '#d8e0ea';
    const textColor = isDarkMode ? DARK_TEXT : '#172033';
    const mutedColor = isDarkMode ? DARK_MUTED : '#6b7688';
    const presetBg = isDarkMode ? DARK_PANEL_DEEP : '#ffffff';
    const presetHover = isDarkMode ? DARK_PANEL_HOVER : '#eef3f8';

    return (
        <div ref={ref} className="relative" style={{ userSelect: 'none' }}>
            <button
                onClick={() => setOpen((o) => !o)}
                className={`flex h-9 items-center gap-2 whitespace-nowrap rounded-lg border px-2.5 text-sm font-medium transition-all outline-none ${isDarkMode
                        ? 'bg-[#0D151F] border-[#404040] text-gray-200 hover:border-[#E55C1A] hover:bg-[#132033] hover:text-white'
                        : 'bg-white border-[#d8e0ea] text-[#334155] hover:border-[#c2410c] hover:text-[#172033] shadow-sm'
                    } ${open ? 'border-[#E55C1A]' : ''}`}
            >
                <CalendarIcon color={isDarkMode ? DARK_MUTED : '#536175'} />
                <span>{label || triggerLabel}</span>
                <ChevronIcon open={open} color={isDarkMode ? DARK_MUTED : '#6b7688'} />
            </button>

            {open && (
                <div
                    style={{
                        position: 'absolute',
                        top: 'calc(100% + 10px)',
                        right: 0,
                        zIndex: 9999,
                        background: dropBg,
                        border: `1px solid ${dropBorder}`,
                        borderRadius: 18,
                        boxShadow: isDarkMode
                            ? '0 22px 45px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.03)'
                            : '0 22px 44px rgba(15,23,42,0.14)',
                        display: 'flex',
                        width: 610,
                        maxWidth: 'calc(100vw - 32px)',
                        overflow: 'hidden',
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    <div
                        style={{
                            background: dropBg,
                            padding: '22px 14px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 6,
                            width: 166,
                            borderRight: `1px solid ${dropBorder}`,
                        }}
                    >
                        {PRESETS.map((p) => {
                            const active = activePreset?.key === p.key;
                            const baseStyle = {
                                background: active
                                    ? (isDarkMode ? THEME_ORANGE_SOFT_DARK : THEME_ORANGE_SOFT_LIGHT)
                                    : presetBg,
                                border: `1px solid ${active
                                        ? (isDarkMode ? 'rgba(229,92,26,0.46)' : 'rgba(194,65,12,0.28)')
                                        : (isDarkMode ? DARK_BORDER : '#dfe7f0')
                                    }`,
                                borderLeft: `3px solid ${active ? (isDarkMode ? THEME_ORANGE : THEME_ORANGE_LIGHT) : 'transparent'}`,
                                borderRadius: 11,
                                padding: '8px 12px',
                                color: active ? (isDarkMode ? '#fed7aa' : '#9a3412') : textColor,
                                fontSize: 12.5,
                                fontWeight: active ? 700 : 500,
                                textAlign: 'left',
                                cursor: 'pointer',
                                transition: 'all 0.15s',
                                whiteSpace: 'nowrap',
                            };

                            return (
                                <button
                                    key={p.key}
                                    onClick={() => handlePreset(p.key)}
                                    style={baseStyle}
                                    onMouseEnter={(e) => {
                                        if (active) return;
                                        e.currentTarget.style.background = presetHover;
                                        e.currentTarget.style.borderColor = isDarkMode ? DARK_BORDER : '#cbd7e6';
                                        e.currentTarget.style.borderLeftColor = isDarkMode ? DARK_BORDER : '#cbd7e6';
                                    }}
                                    onMouseLeave={(e) => {
                                        if (active) return;
                                        e.currentTarget.style.background = presetBg;
                                        e.currentTarget.style.borderColor = isDarkMode ? DARK_BORDER : '#dfe7f0';
                                        e.currentTarget.style.borderLeftColor = 'transparent';
                                    }}
                                >
                                    {p.label}
                                </button>
                            );
                        })}

                        <div style={{ flexGrow: 1 }} />

                        <button
                            onClick={handleReset}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                borderRadius: 11,
                                padding: '8px 12px',
                                color: mutedColor,
                                fontSize: 12.5,
                                fontWeight: 500,
                                textAlign: 'left',
                                cursor: 'pointer',
                                transition: 'background 0.15s',
                                marginTop: 8,
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = isDarkMode ? DARK_PANEL_HOVER : '#eef3f8';
                                e.currentTarget.style.color = textColor;
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent';
                                e.currentTarget.style.color = mutedColor;
                            }}
                        >
                            Zurücksetzen
                        </button>
                    </div>

                    <div style={{ padding: '22px 24px 20px', flex: 1, minWidth: 0 }}>
                        <p
                            style={{
                                fontSize: 10,
                                letterSpacing: '0.14em',
                                fontWeight: 600,
                                color: mutedColor,
                                textTransform: 'uppercase',
                                textAlign: 'center',
                                marginBottom: 8,
                            }}
                        >
                            Zeitraum wählen
                        </p>

                        <p
                            style={{
                                fontSize: 20,
                                fontWeight: 650,
                                color: textColor,
                                textAlign: 'center',
                                marginBottom: 16,
                                letterSpacing: '-0.35px',
                                minHeight: 29,
                                lineHeight: '29px',
                            }}
                        >
                            {headerLabel}
                        </p>

                        <ViewToggle
                            viewMode={viewMode}
                            setViewMode={(mode) => {
                                setViewMode(mode);
                                setHoverDate(null);
                            }}
                            isDarkMode={isDarkMode}
                        />

                        {viewMode === 'day' && (
                            <>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                                    <button
                                        onClick={prevMonth}
                                        style={navButtonStyle(isDarkMode)}
                                        onMouseEnter={(e) => e.currentTarget.style.background = isDarkMode ? DARK_PANEL_HOVER : '#eef3f8'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                    >
                                        ‹
                                    </button>

                                    <button
                                        onClick={() => setViewMode('month')}
                                        style={{
                                            background: isDarkMode ? DARK_PANEL_DEEP : '#ffffff',
                                            border: `1px solid ${dropBorder}`,
                                            cursor: 'pointer',
                                            color: textColor,
                                            padding: '7px 15px',
                                            borderRadius: 12,
                                            fontWeight: 600,
                                            fontSize: 14,
                                        }}
                                    >
                                        {MONTHS_DE[calMonth]} {calYear}
                                    </button>

                                    <button
                                        onClick={nextMonth}
                                        style={navButtonStyle(isDarkMode)}
                                        onMouseEnter={(e) => e.currentTarget.style.background = isDarkMode ? DARK_PANEL_HOVER : '#eef3f8'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                    >
                                        ›
                                    </button>
                                </div>

                                <div style={{ color: textColor }}>
                                    <Calendar
                                        year={calYear}
                                        month={calMonth}
                                        startDate={tempStart}
                                        endDate={tempEnd}
                                        hoverDate={selecting ? hoverDate : null}
                                        onDayClick={handleDayClick}
                                        onDayHover={setHoverDate}
                                        isDarkMode={isDarkMode}
                                    />
                                </div>
                            </>
                        )}

                        {viewMode === 'month' && (
                            <MonthPicker
                                year={calYear}
                                startDate={tempStart}
                                endDate={tempEnd}
                                onMonthClick={handleMonthClick}
                                onYearClick={() => setViewMode('year')}
                                onPrevYear={() => setCalYear((y) => y - 1)}
                                onNextYear={() => setCalYear((y) => y + 1)}
                                isDarkMode={isDarkMode}
                            />
                        )}

                        {viewMode === 'year' && (
                            <YearPicker
                                baseYear={yearPage}
                                startDate={tempStart}
                                endDate={tempEnd}
                                onYearClick={handleYearClick}
                                onPrevYears={() => setYearPage((y) => y - 12)}
                                onNextYears={() => setYearPage((y) => y + 12)}
                                isDarkMode={isDarkMode}
                            />
                        )}

                        <div style={{ marginTop: 18, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                            <p style={{ margin: 0, color: mutedColor, fontSize: 12, fontWeight: 500 }}>
                                {selecting ? 'Ende des Zeitraums wählen' : 'Start und Ende auswählen'}
                            </p>

                            <button
                                onClick={handleApply}
                                disabled={!tempStart || !tempEnd}
                                style={{
                                    background: tempStart && tempEnd ? (isDarkMode ? THEME_ORANGE : THEME_ORANGE_LIGHT) : (isDarkMode ? DARK_PANEL_HOVER : '#dfe7f0'),
                                    color: tempStart && tempEnd ? '#fff' : mutedColor,
                                    border: 'none',
                                    borderRadius: 11,
                                    padding: '8px 22px',
                                    fontWeight: 600,
                                    fontSize: 13.5,
                                    cursor: tempStart && tempEnd ? 'pointer' : 'not-allowed',
                                    transition: 'background 0.2s',
                                    boxShadow: tempStart && tempEnd ? (isDarkMode ? '0 6px 14px rgba(229, 92, 26, 0.20)' : '0 6px 14px rgba(194, 65, 12, 0.18)') : 'none',
                                }}
                                onMouseEnter={(e) => {
                                    if (tempStart && tempEnd) e.currentTarget.style.background = isDarkMode ? THEME_ORANGE_DARK : '#9a3412';
                                }}
                                onMouseLeave={(e) => {
                                    if (tempStart && tempEnd) e.currentTarget.style.background = isDarkMode ? THEME_ORANGE : THEME_ORANGE_LIGHT;
                                }}
                            >
                                Anwenden
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const CalendarIcon = ({ color }) => (
    <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth="2.1"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <rect x="3" y="4" width="18" height="18" rx="3" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
);

const ChevronIcon = ({ open, color }) => (
    <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth="2.3"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
        }}
    >
        <polyline points="6 9 12 15 18 9" />
    </svg>
);
